import { Component, ErrorInfo, ReactNode } from 'react';
import { isWeb3Error, isExtensionConflictError, isRuntimeError } from '../utils/errorHandler';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class Web3ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Only update state if the error is Web3-related, extension conflict, or runtime error
    if (isWeb3Error(error) || isExtensionConflictError(error) || isRuntimeError(error)) {
      return { hasError: true, error };
    }
    // For other errors, let them bubble up normally
    return { hasError: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Only log Web3 errors, extension conflicts, and runtime errors as warnings, not as errors
    if (isWeb3Error(error) || isExtensionConflictError(error) || isRuntimeError(error)) {
      console.warn('Error caught by boundary (handled silently):', error, errorInfo);
      // Don't send to error reporting services for these errors
      return;
    }
    
    // For other errors, log normally and potentially send to error reporting
    console.error('Critical error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error && 
        (isWeb3Error(this.state.error) || isExtensionConflictError(this.state.error) || isRuntimeError(this.state.error))) {
      // For Web3 errors, extension conflicts, and runtime errors, render children normally (silent recovery)
      // This prevents the error from showing to users
      console.warn('Recovering from error silently');
      this.setState({ hasError: false, error: undefined });
      return this.props.children;
    }

    return this.props.children;
  }
}

export default Web3ErrorBoundary;
